import { gql } from '@apollo/client';
import { RCAvatar, RCStarRating } from '@reverbdotcom/cadence/components';
import React from 'react';
import { ProductReviewCardFields } from '../schemas/rql';
import I18n from 'i18n-js';
import { formatInternationalizedDate } from '@reverbdotcom/commons/src/date_utils';
import ProductReviewVoteButton from './ProductReviewVoteButton';
import { SanitizedRender } from '@reverbdotcom/commons';
import ProductReviewFlagButton from './ProductReviewFlagButton';

interface Props {
  review: ProductReviewCardFields.Fragment;
  cspId: string;
  trackingDisplayStyle: 'product' | 'csp';
}

const TRUNCATION_LIMIT = 300;

export function ProductReviewCard({ review, cspId, trackingDisplayStyle }: Props) {
  return (
    <div className="product-review-row-card">
      <div className="product-review-row-card__header">
        <div className="d-flex">
          <RCStarRating
            rating={review.rating / 5}
            hideTooltip
          />
          <div className="ml-2 weight-bold">
            {review.verified &&
              I18n.t('discovery.productReviews.card.verifiedPurchase')
            }
          </div>
        </div>
        <h4 className="product-review-row-card__title">
          {review.title}
        </h4>
        <div className="d-flex fx-justify-between width-100 fx-align-center">
          <div className="product-review-row-card__reviewer">
            <div className="product-review-row-card__avatar">
              <RCAvatar
                userName={review.reviewer.shortname}
                thumbnailURL={review.reviewer.links.avatar.href}
              />
            </div>
            <span>
              {review.reviewer.shortname}
            </span>
          </div>
        </div>
      </div>
      <div className="product-review-row-card__body">
        <div className="product-review-row-card__body-text">
          <SanitizedRender
            html={review.body}
            truncationLimit={TRUNCATION_LIMIT}
          />
        </div>
        <div className="product-review-row-card__body-details">
          <div className="product-review-row-card__body-details-inner">
            {formatInternationalizedDate(review.createdAt)}
          </div>
        </div>
      </div>
      <div className="product-review-row-card__actions">
        <div className="product-review-row-card__action">
          {review.isMyReview || !review.voted &&
            <ProductReviewVoteButton
              reviewId={review.id}
              cspId={cspId}
              trackingDisplayStyle={trackingDisplayStyle}
            />
          }
        </div>
        <div className="product-review-row-card__action">
          {!review.isMyReview &&
            <ProductReviewFlagButton cspId={cspId} productReviewId={review.id} reported={review.reported} />
          }
        </div>
      </div>
      <div className="product-review-row-card__body-details">
        <div className="product-review-row-card__body-details-inner">
          {!!review.voteCount &&
            I18n.t('discovery.productReviews.card.voteCount', { count: review.voteCount })
          }
        </div>
      </div>
    </div>
  );
}

export const ProductReviewCardFragment = gql`
  fragment ProductReviewCardFields on ProductReview {
    _id
    id
    title
    body
    rating
    createdAt
    voteCount
    verified
    voted
    isMyReview
    reported
    reviewer {
      shortname
      links {
        avatar {
          href
        }
      }
    }
  }
`;
